import React, { Fragment, FunctionComponent } from "react";
import SlideProps from "./SlideProps.interface";
import styles from "./Slide.module.scss";

import Button from "components/Button/Button";
import combineClasses from "helpers/combine-classes";
import DynamicHeading from "components/DynamicHeading/DynamicHeading";
import getImageAspectRatio from "app/media/helpers/getImageAspectRatio";
import getImageCrop from "app/media/helpers/getImageCrop";
import ImageViewer from "components/ImageViewer/ImageViewer";
import Paragraph from "components/Paragraph/Paragraph";
import VideoViewer from "components/VideoViewer/VideoViewer";
import VideoEmbed from "components/VideoEmbed/VideoEmbed";
import getVideoAspectRatio from "app/media/helpers/getVideoAspectRatio";
import { forwardRef } from "react";

const Slide = forwardRef<HTMLDivElement, SlideProps>(
  (props, ref: React.Ref<HTMLDivElement>) => {
    // Image
    const image = props.content?.media?.[0]?.content?.image
      ? props.content.media?.[0]?.content
      : undefined;
    const imageCrop = getImageCrop(
      image?.image.umbracoFile.crops,
      image?.imageCrop,
      "Fullscreen (4:3)"
    );
    const imageAspectRatio = getImageAspectRatio(
      imageCrop,
      image?.image.umbracoWidth,
      image?.image.umbracoHeight
    );

    // Video
    const video = props.content?.media?.[0]?.content?.video
      ? props.content.media?.[0]?.content
      : undefined;
    const videoAspectRatio = getVideoAspectRatio(
      video?.video?.[0].content.videoCrop,
      "Widescreen (16:9)"
    );
    const videoSizeClass = video?.videoSize
      ? styles[video.videoSize]
      : undefined;

    const isAutoplay =
      video && video?.video?.[0].content.videoAutoplay
        ? video?.video?.[0].content.videoAutoplay
        : false;
    const isLoop =
      video && video?.video?.[0].content.videoLoop
        ? video?.video?.[0].content.videoLoop
        : false;
    const showControls = !isAutoplay;

    return (
      <div className={styles.wrapper} ref={ref}>
        <div className={combineClasses(styles.inner, videoSizeClass)}>
          {/* Media */}

          {/* Image */}
          {props.content.media &&
          image &&
          image._contentType === "blockImageNestedImage" &&
          image.image?.umbracoFile ? (
            <div className={styles.image_wrapper}>
              <ImageViewer
                className={styles.image}
                src={image.image.umbracoFile.src}
                focalPoint={image.image.umbracoFile.focalPoint}
                aspectRatioX={imageAspectRatio.aspectRatioX}
                aspectRatioY={imageAspectRatio.aspectRatioY}
              />
            </div>
          ) : null}

          {/* Video */}
          {props.content.media &&
          video &&
          video?._contentType === "blockVideoNestedVideo" ? (
            <Fragment>
              {/* Local Video */}
              {video &&
              video.video?.[0].content._contentType ===
                "blockVideoNestedLocal" &&
              video?.video?.[0].content.video.umbracoFile ? (
                <VideoViewer
                  key={props.contentUdi}
                  src={video?.video?.[0].content.video.umbracoFile}
                  alt={video.videoAlternative}
                  autoplay={isAutoplay}
                  loop={isLoop}
                  controls={showControls}
                  aspectRatioX={videoAspectRatio.aspectRatioX}
                  aspectRatioY={videoAspectRatio.aspectRatioY}
                />
              ) : null}

              {/* Video Embed */}
              {video &&
              video.video?.[0].content._contentType ===
                "blockVideoNestedYouTube" &&
              video?.video?.[0].content.videoUrl ? (
                <VideoEmbed
                  key={props.contentUdi}
                  src={video?.video?.[0].content.videoUrl}
                  alt={video.videoAlternative}
                  autoplay={isAutoplay}
                  loop={isLoop}
                  aspectRatioX={16}
                  aspectRatioY={9}
                />
              ) : null}

              {/* Caption */}
              {video.videoCaption && video.videoCaption.length > 0 ? (
                <div className={styles.caption}>{video.videoCaption}</div>
              ) : null}
            </Fragment>
          ) : null}

          {/* Content */}
          <div className={styles.content}>
            {props.content.heading ? (
              <DynamicHeading
                className={styles.heading}
                headlineSize={"H3"}
                headlineWeight={"H3"}
              >
                {props.content.heading}
              </DynamicHeading>
            ) : null}

            {props.content.bodyText ? (
              <Paragraph
                className={styles.text}
                innerHTML={props.content.bodyText}
              />
            ) : null}

            {/* Buttons */}
            {props.content.buttons && props.content.buttons.length > 0 ? (
              <div className={styles.buttons}>
                {props.content.buttons.map((button, index) => {
                  return (
                    <Button
                      key={index}
                      to={button.content.link}
                      variant={button.content.variant}
                      {...button.content}
                    >
                      {button.content.label}
                    </Button>
                  );
                })}
              </div>
            ) : null}
          </div>
        </div>
      </div>
    );
  }
);

export default Slide;
